import { z } from "zod";

const translationPath = "page.assessments.questions.errors.";

const questionCategorySchema = z.object(
  {
    id: z.string(),
    name: z.string(),
    parentId: z.string().optional().nullable(),
  },
  { invalid_type_error: translationPath + "question_category" },
);

const questionOptionTypeSchema = z.object({
  id: z.string().optional(),
  isRequiredJustification: z.boolean().optional(),
  requiredJustificationLessThan: z.string().optional().nullable(),
  requiredJustificationOptions: z.string().optional().nullable(),
  requiredJustificationMinChars: z.string().optional().nullable(),
  requiredJustificationMessage: z.string().optional().nullable(),
  alwaysShowJustification: z.boolean().nullish(),
  minChars: z.string().optional().nullable(),
  isRequired: z.boolean().optional().nullable(),
});

const questionAlternativeSchema = z.object({
  id: z.string().nullish(),
  questionEvaluationTypeAlternativeId: z.string().nullish(),
  nrOption: z.number(),
  displayOrder: z.number(),
  integrationId: z.string().nullable(),
  label: z.string().nullable(),
});

const questionEvaluationTypeSchema = z.object({
  id: z.string(),
  name: z.string(),
  code: z.string().optional().nullable(),
});

const guidanceTextPositionSchema = z.object({
  id: z.string(),
  name: z.string(),
});

export const questionSchema = z
  .object({
    id: z.string().optional(),
    questionCategory: questionCategorySchema,
    questionOptionType: questionOptionTypeSchema.optional(),
    questionAlternatives: z.array(questionAlternativeSchema).optional(),
    questionEvaluationType: questionEvaluationTypeSchema,
    title: z.string().min(1, translationPath + "title"),
    description: z.string().nullable(),
    integrationId: z.string().min(1, translationPath + "integration_id"),
    justificationIntegrationId: z.string().nullish(),
    guidanceText: z.string().nullish(),
    guidanceTextPosition: guidanceTextPositionSchema.nullish(),
  })
  .superRefine((val, ctx) => {
    if (val.questionOptionType?.isRequiredJustification) {
      const isSimpleChoice = val.questionEvaluationType.code === "11";

      if (
        isSimpleChoice &&
        (!(val.questionOptionType.requiredJustificationOptions ?? "").trim() ||
          (val.questionOptionType.requiredJustificationOptions ?? "")
            .split(",")
            .some((option) => !option.trim()))
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: translationPath + "required_justification_options",
          path: ["questionOptionType", "requiredJustificationOptions"],
        });
      }

      if (
        !isSimpleChoice &&
        !val.questionOptionType.requiredJustificationLessThan
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: translationPath + "required_justification_less_than",
          path: ["questionOptionType", "requiredJustificationLessThan"],
        });
      }

      if (!val.questionOptionType.requiredJustificationMessage) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: translationPath + "required_justification_message",
          path: ["questionOptionType", "requiredJustificationMessage"],
        });
      }

      if (!val.questionOptionType.requiredJustificationMinChars) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: translationPath + "required_justification_min_chars",
          path: ["questionOptionType", "requiredJustificationMinChars"],
        });
      }
    }

    const requiresMinChars =
      val.questionOptionType?.isRequired &&
      val.questionEvaluationType.code !== "11";

    if (requiresMinChars && !val.questionOptionType?.minChars) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: translationPath + "min_chars",
        path: ["questionOptionType", "minChars"],
      });
    }
  });

export type TQuestionForm = z.infer<typeof questionSchema>;
